import {
  FilterOption,
  FilterSelect,
  FilterSelectSearchable,
} from "@components";
import { Feature, ListDataCategory, Role } from "@enums";
import {
  Add as AddIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  KeyboardDoubleArrowRight as KeyboardDoubleArrowRightIcon,
  LocationCity as LocationCityIcon,
  PeopleAlt as PeopleAltIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import CreateCareplanModal from "@pages/CommandCenter/CreateCareplanModal";
import ImportAriaPrescriptionsModal from "@pages/CommandCenter/ImportAriaPrescriptionsModal";
import {
  AssignablePlannerOut,
  ListDataOptionSerialisedOut,
  useProtocolApiV1ListDataQuery,
  UserOptionOut,
  useWebApiV1GetAllAssignablePlannersQuery,
  useWebApiV1GetTenantPhysiciansQuery,
  useWebApiV1GetUserFeatureSwitchesQuery,
} from "@providers/hop-ord-server/api";
import { useCheckUserRole } from "@utils";
import { useMemo, useState } from "react";
import { SiteOption } from "./types";
import { useTreatingClinics } from "./utils";

export const SearchFilterOption = {
  DRAFT: "Draft careplans",
  NEW_CAREPLAN: "New careplans",
  READY_FOR_PLANNING: "Ready for planning",
  PLANNING_AUTOMATION: "Planning (Automation)",
  HANDOVER_COMPLETE: "Handover complete",
  PENDING_PLAN_REVIEW: "Pending plan review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

const sortByName = (a: string, b: string) => a.localeCompare(b);

const STATUS_OPTIONS: FilterOption[] = [
  { label: "Draft", value: SearchFilterOption.DRAFT },
  { label: "Not ready for planning", value: SearchFilterOption.NEW_CAREPLAN },
  { label: "Ready for planning", value: SearchFilterOption.READY_FOR_PLANNING },
  {
    label: "Planning (automation)",
    value: SearchFilterOption.PLANNING_AUTOMATION,
  },
  {
    label: "Pending plan review",
    value: SearchFilterOption.PENDING_PLAN_REVIEW,
  },
  { label: "Approved", value: SearchFilterOption.APPROVED },
  { label: "Rejected", value: SearchFilterOption.REJECTED },
  { label: "Handover complete", value: SearchFilterOption.HANDOVER_COMPLETE },
];
const STATUS_OPTIONS_RO: FilterOption[] = [
  {
    label: "Pending plan review",
    value: SearchFilterOption.PENDING_PLAN_REVIEW,
  },
  { label: "Approved", value: SearchFilterOption.APPROVED },
  { label: "Rejected", value: SearchFilterOption.REJECTED },
];

const QUICK_FITLERS: { label: string; value: string; tooltip?: string }[] = [
  { label: "Unread comments", value: "Unread comments" },
  { label: "Test view", value: "Test view", tooltip: "Show test cases only" },
];

interface Props {
  searchText: string;
  setSearchText: (searchText: string) => void;
  selectedStatus: string[];
  setSelectedStatus: (selectedStatus: string[]) => void;
  selectedSite: string[];
  setSelectedSite: (selectedSite: string[]) => void;
  selectedPhysician: string[];
  setSelectedPhysician: (selectedPhysician: string[]) => void;
  selectedQuickFilters: string[];
  setSelectedQuickFilters: (selectedQuickFilters: string[]) => void;
  selectedAssignedTo: string[];
  setSelectedAssignedTo: (selectedAssignedTo: string[]) => void;
  tenantIds: number[];
}

const SearchAndFilterBar = ({
  searchText,
  setSearchText,
  selectedStatus,
  setSelectedStatus,
  selectedSite,
  setSelectedSite,
  selectedPhysician,
  setSelectedPhysician,
  selectedQuickFilters,
  setSelectedQuickFilters,
  selectedAssignedTo,
  setSelectedAssignedTo,
  tenantIds,
}: Props) => {
  const { data: treatingDepartments } = useProtocolApiV1ListDataQuery({
    categories: [ListDataCategory.TREATING_DEPARTMENT],
    tenantIds: tenantIds ?? [],
  });
  const [importRxDialogOpen, setImportRxDialogOpen] = useState(false);
  const [isCreateCareplanDialogOpen, setCreateCareplanDialogOpen] =
    useState(false);
  const treatingClinics = useTreatingClinics(tenantIds ?? []);
  const { data: physicians } = useWebApiV1GetTenantPhysiciansQuery({});
  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();
  const { data: assignablePlanners = [] } =
    useWebApiV1GetAllAssignablePlannersQuery();

  // const { user: auth0User } = useAuth0();
  // const currentUserName = auth0User?.name ?? "";

  const currentPlanner = useMemo(
    () =>
      assignablePlanners.find((p: AssignablePlannerOut) =>
        p.fullName.includes(" (Me)"),
      ),
    [assignablePlanners],
  );

  const assignedToOptions: FilterOption[] = useMemo(() => {
    const plannerOptions: FilterOption[] = [...assignablePlanners].map(
      (p: AssignablePlannerOut) => ({
        label: p.fullName,
        value: String(p.id),
      }),
    );
    return [...plannerOptions];
  }, [assignablePlanners]);

  const isRo = useCheckUserRole(Role.RO);
  const showCreateOrderButton =
    featureSwitchData?.find(
      (feature) => feature.name === Feature.COMMAND_CENTER_CREATE_ORDER,
    ) !== undefined && !isRo;

  const showImportRxButton =
    featureSwitchData?.find(
      (feature) => feature.name === Feature.IGUANA_INTEGRATION,
    ) !== undefined;

  const handleCreateOrderClick = () => {
    setCreateCareplanDialogOpen(true);
  };

  const siteOptions =
    treatingDepartments
      ?.map((listData: ListDataOptionSerialisedOut): SiteOption => {
        // If a value appears twice in the list, we need to show the tenant name as well.
        // Only do this for items that are duplicated at least twice.
        if (
          treatingDepartments?.filter(
            (option) => option?.value === listData?.value,
          ).length >= 2
        ) {
          const tenantName = listData?.tenantName.split(":").at(-1);
          return {
            value: listData?.value,
            label: listData?.value + " (" + tenantName + ")",
            tenantId: listData?.tenantId,
          };
        }
        return {
          value: listData?.value,
          label: listData?.value,
          tenantId: listData?.tenantId,
        };
      })
      .sort((a, b) => sortByName(a.label, b.label)) || [];

  const physicianOptions =
    physicians
      ?.map((physician: UserOptionOut) => ({
        label: physician?.name,
        value: String(physician?.id),
      }))
      .sort((a, b) => sortByName(a.label, b.label)) || [];

  const clearAllFilters = () => {
    setSelectedStatus([]);
    setSelectedSite([]);
    setSelectedPhysician([]);
    setSelectedQuickFilters([]);
    setSelectedAssignedTo([]);
  };

  const anyFilterSelected =
    selectedStatus.length > 0 ||
    selectedSite.length > 0 ||
    selectedPhysician.length > 0 ||
    selectedQuickFilters.length > 0 ||
    selectedAssignedTo.length > 0;

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 3,
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 1,
          }}
        >
          {showCreateOrderButton && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateOrderClick}
            >
              Create Careplan
            </Button>
          )}
          {showImportRxButton && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ExitToAppIcon />}
              onClick={() => setImportRxDialogOpen(true)}
            >
              Import
            </Button>
          )}
        </Stack>
        <TextField
          id="search-text"
          placeholder="Search patient name or ID"
          variant="standard"
          value={searchText}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(event.target.value);
          }}
          sx={{
            width: "250px",
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchText && (
                    <IconButton
                      onClick={() => setSearchText("")}
                      color="secondary"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
        <Stack
          direction="row"
          sx={{
            gap: 1,
          }}
        >
          <FilterSelect
            label="Planning status"
            options={isRo ? STATUS_OPTIONS_RO : STATUS_OPTIONS}
            selectedOptions={selectedStatus}
            setSelectedOptions={setSelectedStatus}
            startIcon={<KeyboardDoubleArrowRightIcon />}
          />
          <FilterSelect
            label="Site"
            options={siteOptions}
            selectedOptions={selectedSite}
            setSelectedOptions={setSelectedSite}
            startIcon={<LocationCityIcon fontSize="small" />}
            hideTextWhenSmall
          />
          <FilterSelect
            label="Physician"
            options={physicianOptions}
            selectedOptions={selectedPhysician}
            setSelectedOptions={setSelectedPhysician}
            startIcon={<PersonIcon fontSize="small" />}
            hideTextWhenSmall
          />
          <FilterSelectSearchable
            label="Assigned to"
            options={assignedToOptions}
            selectedOptions={selectedAssignedTo}
            setSelectedOptions={setSelectedAssignedTo}
            startIcon={<PeopleAltIcon fontSize="small" />}
            hideTextWhenSmall
            pinnedOptionValues={
              currentPlanner ? [String(currentPlanner.id)] : []
            }
          />
          <FilterSelect
            label="Quick Filters"
            options={QUICK_FITLERS}
            selectedOptions={selectedQuickFilters}
            setSelectedOptions={setSelectedQuickFilters}
            startIcon={<FilterListIcon fontSize="small" />}
            hideTextWhenSmall
          />
          {anyFilterSelected && (
            <Button
              data-testid="clear-filters-button"
              disabled={!anyFilterSelected}
              startIcon={<ClearIcon fontSize="small" />}
              onClick={clearAllFilters}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Stack>
      <CreateCareplanModal
        open={isCreateCareplanDialogOpen}
        onClose={() => setCreateCareplanDialogOpen(false)}
        treatingClinics={treatingClinics}
      />
      <ImportAriaPrescriptionsModal
        open={importRxDialogOpen}
        onClose={() => setImportRxDialogOpen(false)}
        treatingClinics={treatingClinics}
      />
    </>
  );
};

export default SearchAndFilterBar;
